import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { CheckCircle, Calendar, Building2, Euro, TrendingUp, Clock, RefreshCw, Search, Trash2, CheckCheck, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SouscriptionsValidation() {
  const { getAuthHeader } = useAuth();
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => { fetchSouscriptions(); }, []);

  const fetchSouscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/souscriptions-rcd`, { headers: getAuthHeader() });
      const enAttente = response.data
        .filter(s => s.statut === 'en_attente_paiement' || s.statut === 'signee')
        .sort((a, b) => new Date(b.date_creation || 0) - new Date(a.date_creation || 0));
      setSouscriptions(enAttente);
      setSelectedIds(new Set());
    } catch {
      toast.error('Erreur lors du chargement des souscriptions');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const montantTotal = souscriptions.reduce((sum, s) => sum + (s.montant_ht_contribution || 0), 0);
    return { total: souscriptions.length, montantTotal, avgAmount: souscriptions.length > 0 ? montantTotal / souscriptions.length : 0 };
  }, [souscriptions]);

  const filtered = useMemo(() => {
    if (!searchTerm) return souscriptions;
    const q = searchTerm.toLowerCase().trim();
    return souscriptions.filter(s => {
      // Raison sociale, n° police, SIRET, email
      if ((s.raison_sociale || '').toLowerCase().includes(q)) return true;
      if ((s.numero_police || '').toLowerCase().includes(q)) return true;
      if ((s.siret || '').includes(searchTerm)) return true;
      if ((s.email || '').toLowerCase().includes(q)) return true;
      // V36.90 — Recherche par territoire (string CSV OU liste)
      const raw = s.territoires || s.departements || s.territoire || s.departement;
      const territoires = Array.isArray(raw)
        ? raw.map(String)
        : (typeof raw === 'string' ? raw.split(',').map(t => t.trim()) : []);
      if (territoires.some(t => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [souscriptions, searchTerm]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleSelect = (e, id) => { e.stopPropagation(); setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const isAllSelected = filtered.length > 0 && filtered.every(s => selectedIds.has(s.id));
  const toggleSelectAll = () => { if (isAllSelected) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map(s => s.id))); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const handleValiderPaiement = async (souscriptionId) => {
    if (!window.confirm('Confirmer la validation du paiement ?')) return;
    setProcessing(souscriptionId);
    try {
      await axios.post(`${API}/souscriptions-rcd/${souscriptionId}/valider-paiement`,
        { reference_paiement: `VIREMENT-${new Date().toISOString().slice(0, 10)}`, mode_paiement: 'virement' },
        { headers: getAuthHeader() });
      toast.success('Paiement valide avec succes !');
      fetchSouscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally { setProcessing(null); }
  };

  const handleBulkValidate = async () => {
    if (selectedIds.size === 0) { toast.error('Selectionnez au moins un paiement'); return; }
    if (!window.confirm(`Valider ${selectedIds.size} paiement(s) ?`)) return;
    setBulkProcessing(true);
    let ok = 0, err = 0;
    for (const id of selectedIds) {
      try {
        await axios.post(`${API}/souscriptions-rcd/${id}/valider-paiement`,
          { reference_paiement: `VIREMENT-BULK-${new Date().toISOString().slice(0, 10)}`, mode_paiement: 'virement' },
          { headers: getAuthHeader() });
        ok++;
      } catch { err++; }
    }
    setBulkProcessing(false);
    if (ok > 0) toast.success(`${ok} paiement(s) valide(s)`);
    if (err > 0) toast.error(`${err} erreur(s)`);
    fetchSouscriptions();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) { toast.error('Selectionnez au moins un paiement'); return; }
    if (!window.confirm(`Supprimer ${selectedIds.size} paiement(s) ? Action irreversible.`)) return;
    setBulkProcessing(true);
    let ok = 0, err = 0;
    for (const id of selectedIds) {
      try { await axios.delete(`${API}/souscriptions-rcd/${id}`, { headers: getAuthHeader() }); ok++; } catch { err++; }
    }
    setBulkProcessing(false);
    if (ok > 0) toast.success(`${ok} paiement(s) supprime(s)`);
    if (err > 0) toast.error(`${err} erreur(s)`);
    fetchSouscriptions();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="validation-paiements">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Validation des Paiements</h2>
          <p className="text-sm text-gray-500">Centre de controle financier en temps reel</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSouscriptions} className="gap-1.5">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Actualiser
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'En attente', value: stats.total, icon: Clock, color: 'text-amber-600' },
          { label: 'Montant total', value: `${stats.montantTotal.toLocaleString('fr-FR')} \u20AC`, icon: Euro, color: 'text-emerald-600' },
          { label: 'Moyenne', value: `${stats.avgAmount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} \u20AC`, icon: TrendingUp, color: 'text-blue-600' },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white">
            <s.icon className={cn("h-4 w-4 shrink-0", s.color)} />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">{s.label}</p>
              <p className={cn("text-lg font-bold", s.color)}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input placeholder="Rechercher par raison sociale, N° police, SIRET, email, territoire..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="validation-search" />
      </div>

      {/* Select all + bulk actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            <input type="checkbox" checked={isAllSelected} readOnly className="w-4 h-4 rounded border-gray-300 text-amber-600" />
            <span>{isAllSelected ? 'Tout deselectionner' : 'Tout selectionner'}</span>
          </button>
          <span className="text-sm text-gray-400">{filtered.length} resultat(s)</span>
          {selectedIds.size > 0 && <Badge className="bg-amber-600 text-white font-semibold">{selectedIds.size} selectionnee(s)</Badge>}
        </div>
        {selectedIds.size > 0 && (
          <div className="flex gap-1.5">
            <Button size="sm" onClick={handleBulkValidate} disabled={bulkProcessing} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs gap-1">
              {bulkProcessing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />} Valider ({selectedIds.size})
            </Button>
            <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkProcessing} className="h-8 text-xs gap-1">
              {bulkProcessing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Supprimer ({selectedIds.size})
            </Button>
          </div>
        )}
      </div>

      {/* Liste depliable */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <CheckCircle className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucun paiement en attente</p>
          <p className="text-sm text-gray-400">Tous les paiements ont ete valides</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="validation-list">
          {filtered.map((s) => {
            const isExpanded = expandedIds.has(s.id);
            const isSelected = selectedIds.has(s.id);
            return (
              <div key={s.id} className={cn("rounded-lg border transition-all duration-150",
                isSelected ? "border-amber-300 bg-amber-50/30" : isExpanded ? "border-blue-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
              )} data-testid={`validation-row-${s.id}`}>
                {/* Ligne compacte */}
                <div className="flex items-center gap-3 px-4 py-2.5">
                  <input type="checkbox" checked={isSelected} onChange={(e) => toggleSelect(e, s.id)}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 shrink-0" />
                  <button onClick={() => toggleExpand(s.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`validation-toggle-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={cn("font-medium text-sm truncate", isExpanded ? "text-blue-700" : "text-gray-900")}>{s.raison_sociale}</p>
                        {s.type_document === 'avenant' && <Badge className="bg-purple-700 text-white border border-purple-900 text-[9px] px-1 py-0 font-semibold">AVENANT</Badge>}
                      </div>
                      <p className="text-[10px] text-gray-400 font-mono truncate">{s.numero_police?.slice(0, 25)}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      <Building2 className="h-3 w-3" /><span className="font-mono">{s.siret?.slice(0, 9)}...</span>
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      <Calendar className="h-3 w-3" /><span>{formatDate(s.date_creation)}</span>
                    </div>
                    <Badge className="bg-amber-600 text-white border border-amber-800 text-[10px] shrink-0 font-semibold">
                      <Clock className="h-2.5 w-2.5 mr-0.5" />En attente
                    </Badge>
                    <span className="text-sm font-bold text-amber-700 tabular-nums shrink-0">{(s.montant_ht_contribution || 0).toLocaleString('fr-FR')}&euro;</span>
                    <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-blue-500")} />
                  </button>
                </div>

                {/* Panneau depliant */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`validation-details-${s.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Col 1: Entreprise */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Entreprise</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900" data-testid={`validation-raison-${s.id}`}>{s.raison_sociale}</p>
                          <p className="text-gray-600 flex items-center gap-1.5" data-testid={`validation-siret-${s.id}`}><Building2 className="h-3.5 w-3.5 text-gray-400" />SIRET: <span className="font-mono">{s.siret || '-'}</span></p>
                          {/* V36.89 — Territoires (string CSV ou liste) */}
                          {(() => {
                            const raw = s.territoires || s.departements || s.territoire || s.departement;
                            let list = [];
                            if (Array.isArray(raw)) list = raw.filter(Boolean).map(String);
                            else if (typeof raw === 'string') list = raw.split(',').map(x => x.trim()).filter(Boolean);
                            if (!list.length) return null;
                            return (
                              <p className="text-gray-600 flex items-center gap-1.5" data-testid={`validation-territoires-${s.id}`}>
                                <span className="text-gray-400">Territoires:</span>
                                <span>{list.join(' · ')}</span>
                              </p>
                            );
                          })()}
                          {s.email && <p className="text-gray-600 truncate" data-testid={`validation-email-${s.id}`}>{s.email}</p>}
                          {s.telephone && <p className="text-gray-600">{s.telephone}</p>}
                        </div>
                      </div>

                      {/* Col 2: Details */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details souscription</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">N police:</span>
                            <span className="font-mono text-xs text-gray-800 truncate max-w-[180px]" title={s.numero_police}>{s.numero_police}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Type:</span>
                            <span className="text-gray-700">{s.type_souscription || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Date creation:</span>
                            <span className="text-gray-700">{formatDate(s.date_creation)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Montant de base:</span>
                            <span className="font-bold text-amber-700">{(s.montant_ht_contribution || 0).toLocaleString('fr-FR')} &euro;</span>
                          </div>
                          {s.mode_paiement && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Mode paiement:</span>
                              <span className="text-gray-700 capitalize">{s.mode_paiement}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Col 3: Action */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Action</h4>
                        <Button
                          onClick={() => handleValiderPaiement(s.id)}
                          disabled={processing === s.id || bulkProcessing}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
                          data-testid={`validate-paiement-${s.id}`}
                        >
                          {processing === s.id ? (
                            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Validation...</>
                          ) : (
                            <><CheckCircle className="h-4 w-4 mr-2" />Valider le paiement</>
                          )}
                        </Button>
                        <div className="bg-emerald-50 rounded-lg border border-emerald-100 p-2.5">
                          <p className="text-[11px] text-emerald-700 leading-relaxed">
                            La validation confirme la reception du virement et active la souscription.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
